import React, { useEffect, useState } from "react";
import LoadingWheel from "../../components/Shared/LoadingWheel";
import { withAgency } from "../../data/withAgency";
import { PageHeader } from "../../components/Shared/PageHeader";
import { useNewsletterStatistics } from "../../data/newsletters/useNewsletterStatistics";
import { BaseCard } from "../../components/NurturingCampaign/BaseCard";
import { useForm } from "react-hook-form";
import { StyledTextInput } from "../../components/Shared/StyledInputs/StyledTextInput";
import { ButtonInput } from "../../components/Shared/Inputs/ButtonInput";
import { NewsletterStatsDisplay } from "../../components/NewsletterBuilder/NewsletterStatsDisplay";

interface NewsletterSelectionFormFields {
  month: string;
}

interface NewsletterStatsProps {
  agency: any;
  agencyReady: boolean;
}

function getYearMonthString(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${year}-${month}`;
}

function _NewsletterStats({ agency, agencyReady }: NewsletterStatsProps) {
  const [date, setDate] = useState<Date>(new Date());
  const { register, handleSubmit, watch } = useForm<NewsletterSelectionFormFields>({
    defaultValues: { month: getYearMonthString(date) },
  });
  const { statistics } = useNewsletterStatistics(agency.id, date.toISOString());
  const debounceRef = React.useRef<number | null>(null);
  const initialMount = React.useRef(true);
  const watchedMonth = watch("month");

  const onSubmit = React.useCallback((data: { month?: string }) => {
    if (data.month) {
      const dateParts = data.month.split('-');
      const newDate = new Date();
      newDate.setFullYear(parseInt(dateParts[0]));
      newDate.setMonth(parseInt(dateParts[1]) - 1);

      setDate(newDate);
    }
  }, []);

  useEffect(() => {
    if (initialMount.current) {
      initialMount.current = false;
      return;
    }

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = window.setTimeout(() => {
      handleSubmit(onSubmit)();
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [watchedMonth, handleSubmit, onSubmit]);

  if (!agencyReady || statistics.isLoading) {
    return <div className="flex py-10 justify-center"><LoadingWheel width="w-10" height="h-10" /></div>
  } else {
    return (
      <div className="px-4 sm:px-6 lg:px-8 space-y-6">
        <PageHeader
          title="Newsletter Statistics"
          description="View and analyze your newsletter performance"
        />
        <BaseCard title="Choose Newsletter">
          <form onSubmit={handleSubmit(onSubmit)} className="flex items-center space-x-4">
            <StyledTextInput
              type="month"
              classes="w-96"
              registerName="month"
              register={register}
            />
          </form>
        </BaseCard>
        <NewsletterStatsDisplay stat={statistics.data?.data!} date={date} />
      </div>
    );
  }
}

const NewsletterStats = withAgency(_NewsletterStats);

export default NewsletterStats;
